import React from 'react';
import { useForm } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import { Card, Message, ToggleContainer } from '@weezevent/nacre';
import Style from './displayModuleMessages.module.css';
import PropTypes from 'prop-types';
import { getLanguageOptions } from '../../../nacre/components/LanguageSelector';
import { DisplayTranslatedMessage } from './displayTranslatedMessage';
import { getPipeTranslationByLocale } from '../../../components/utils/pipeTranslateConvert';
import { unifyLanguageCodes } from '../../../components/utils/locales';

export const DisplayModuleMessages = ({ formModuleDisplay, toggleStates, setToggleStates = () => {}, setFormModuleDisplay, languages, defaultLang }) => {
    const { t, i18n } = useTranslation();

    const keys = ['instructions_form', 'text_confirmation_inscription', 'text_perso_no_ticket', 'text_perso_quota_reached'];

    const languageOptions = React.useMemo(() => getLanguageOptions().filter(elt => unifyLanguageCodes(languages).some(lng => lng === elt.value)), [languages]);

    const getListLanguages = React.useCallback(
        () =>
            languageOptions.map(elt => {
                const defaultMessages = keys.reduce((messages, key) => {
                    const wordingKey = `ticket.ticket_option.module.message_information.${key}_default`;
                    messages[key] = t(wordingKey, { lng: elt.value });
                    return messages;
                }, {});
                return {
                    label: elt.text,
                    value: elt.value,
                    default: defaultMessages
                };
            }),
        [languageOptions, t]
    );
    const [listLanguage, setListLanguage] = React.useState(getListLanguages());

    React.useEffect(() => {
        // reload resources with all languages to display all available default messages
        i18n.reloadResources(unifyLanguageCodes(languages)).finally(() => {
            setListLanguage(getListLanguages());
        });
    }, [i18n, getListLanguages, languages]);

    // @todo Now we have some problem with available_languages, this will be solved with other fix that users have to set Pay when they create their organisation
    // Another pull request Menhir will also resolve this problem
    const defaultLanguage = React.useMemo(() => {
        const hasDefaultLang = languageOptions.some(obj => obj.value === defaultLang);
        if (hasDefaultLang) {
            return defaultLang;
        }
        return languageOptions[0].value;
    }, [languageOptions, defaultLang]);

    let valuesArray = React.useMemo(
        () =>
            listLanguage.reduce((result, locale) => {
                var langKey = locale.value;
                const values = keys.reduce((allValues, key) => {
                    const foundTranslation = getPipeTranslationByLocale(formModuleDisplay[key], langKey);
                    if (!formModuleDisplay[key] && !toggleStates[key]) allValues[key] = locale.default[key];
                    else allValues[key] = foundTranslation;
                    return allValues;
                }, {});

                result[langKey] = values;
                return result;
            }, {}),
        [listLanguage, toggleStates, formModuleDisplay]
    );

    const defaultTranslateDropdownsLanguage = React.useMemo(
        () =>
            keys.reduce((obj, key) => {
                obj[key] = defaultLanguage;
                return obj;
            }, {}),
        [defaultLanguage]
    );
    const [translatesCurrentLanguage, setTranslatesCurrentLanguage] = useForm(defaultTranslateDropdownsLanguage);

    const getValueWithFallBack = (key, valuesArrayLangSelected) => {
        if (!valuesArrayLangSelected || !Object.keys(valuesArrayLangSelected).includes(key)) {
            return '';
        }
        return valuesArrayLangSelected[key];
    };
    const defaultValues = React.useMemo(
        () =>
            keys.reduce((obj, key) => {
                // get default value for each key in the current translateDropdown language
                obj[key] = getValueWithFallBack(key, valuesArray[translatesCurrentLanguage[key]]);
                return obj;
            }, {}),
        [translatesCurrentLanguage, valuesArray]
    );

    const onToggle = React.useCallback(
        (keyMessage, toggleState) => {
            if (toggleState) {
                const ret = [];
                listLanguage.forEach(language => {
                    if (valuesArray[language.value][keyMessage]) ret.push(`[${language.value}]${valuesArray[language.value][keyMessage]}`);
                });
                // need to set formModuleDisplay value on toggle otherwise it's only updated on textArea change
                setFormModuleDisplay(keyMessage, ret.join('|'));
            } else {
                setTranslatesCurrentLanguage(keyMessage, defaultLanguage);
            }
            setToggleStates(keyMessage, toggleState);
        },
        [setToggleStates, setFormModuleDisplay, listLanguage, defaultLanguage, valuesArray]
    );

    return (
        <Card.FormStep
            className={Style['form-display--textarea']}
            title={t('ticket.ticket_option.module.message_information.title')}
            rightComponent={
                <Message type="tips" title={t('ticket.ticket_option.module.tips')} mini>
                    {t(`ticket.ticket_option.module.infoTips`)}
                </Message>
            }
            id={'message-information'}
        >
            <>
                {keys.map((keyMessage, index) => (
                    <ToggleContainer
                        toggle={toggleStates[keyMessage]}
                        onToggle={state => onToggle(keyMessage, state)}
                        key={index}
                        className={Style['display-module-messages']}
                        title={`${t(`ticket.ticket_option.module.message_information.${keyMessage}`)}`}
                        subtitle={`${t(`ticket.ticket_option.module.message_information.displayed.${keyMessage}`)}`}
                    >
                        <DisplayTranslatedMessage
                            keyMessage={keyMessage}
                            valueTextArea={defaultValues[keyMessage]}
                            valuesArray={valuesArray}
                            defaultLanguage={defaultLanguage}
                            languageSelected={translatesCurrentLanguage[keyMessage]}
                            setLanguageSelected={lng => setTranslatesCurrentLanguage(keyMessage, lng)}
                            listLanguage={listLanguage}
                            setFormModuleDisplay={setFormModuleDisplay}
                        />
                    </ToggleContainer>
                ))}
            </>
        </Card.FormStep>
    );
};

DisplayModuleMessages.propTypes = {
    formModuleDisplay: PropTypes.object.isRequired,
    setFormModuleDisplay: PropTypes.func.isRequired,
    translateIdtoChoice: PropTypes.func,
    languages: PropTypes.array
};

export default DisplayModuleMessages;
